/**
 * Referrals Service
 *
 * Handles referrals-related API calls.
 */

import api, { getApiUrl, getAuthToken } from '../utils/api';

const buildReferralQuery = (params = {}) => {
  const query = {};
  if (params.skip !== undefined) query.skip = params.skip;
  if (params.limit !== undefined) query.limit = params.limit;
  if (params.search) query.search = params.search;
  if (params.status_filter) query.status_filter = params.status_filter;
  return query;
};

const normalizeReferralPayload = (payload = {}) => {
  const out = {};
  if (payload.sn !== undefined) out.sn = payload.sn;
  if (payload.name !== undefined) out.name = payload.name?.trim() || '';
  if (payload.email !== undefined) out.email = payload.email?.trim() || '';
  if (payload.phone_number !== undefined) out.phone_number = payload.phone_number?.trim() || '';
  if (payload.referral_code !== undefined) out.referral_code = payload.referral_code?.trim() || '';
  if (payload.processing_status !== undefined) out.processing_status = payload.processing_status;
  if (payload.failure_reason !== undefined) out.failure_reason = payload.failure_reason;
  return out;
};

/**
 * Get all referrals
 * GET /v1/referrals/?skip=0&limit=20
 * @param {Object} params - Query params { skip?: number, limit?: number }
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const getReferrals = async (params = {}) => {
  try {
    const query = buildReferralQuery(params);

    const response = await api.get('/v1/referrals/', query);

    if (response?.status === 'success' && response?.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Referrals retrieved successfully',
      };
    }

    return {
      success: false,
      error: response?.message || 'Failed to retrieve referrals',
      data: null,
    };
  } catch (error) {
    console.error('Get referrals error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to retrieve referrals. Please try again.';

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Create referral
 * POST /v1/referrals/
 * @param {Object} payload - Referral payload
 */
export const createReferral = async (payload) => {
  try {
    const response = await api.post('/v1/referrals/', normalizeReferralPayload(payload));

    if (response?.status === 'success' && response?.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Referral created successfully',
      };
    }

    return {
      success: false,
      error: response?.message || 'Failed to create referral',
      data: null,
    };
  } catch (error) {
    console.error('Create referral error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to create referral. Please try again.';

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get referral by id
 * GET /v1/referrals/{referral_id}
 */
export const getReferral = async (referralId) => {
  try {
    const response = await api.get(`/v1/referrals/${referralId}`);

    if (response?.status === 'success' && response?.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Referral retrieved successfully',
      };
    }

    return {
      success: false,
      error: response?.message || 'Failed to retrieve referral',
      data: null,
    };
  } catch (error) {
    console.error('Get referral error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to retrieve referral. Please try again.';

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Update referral
 * PUT /v1/referrals/{referral_id}
 */
export const updateReferral = async (referralId, payload) => {
  try {
    const response = await api.put(`/v1/referrals/${referralId}`, normalizeReferralPayload(payload));

    if (response?.status === 'success' && response?.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Referral updated successfully',
      };
    }

    return {
      success: false,
      error: response?.message || 'Failed to update referral',
      data: null,
    };
  } catch (error) {
    console.error('Update referral error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to update referral. Please try again.';

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Delete referral
 * DELETE /v1/referrals/{referral_id}
 */
export const deleteReferral = async (referralId) => {
  try {
    const response = await api.delete(`/v1/referrals/${referralId}`);

    if (response?.status === 'success' || response?.message) {
      return {
        success: true,
        message: response.message || 'Referral deleted successfully',
      };
    }

    return {
      success: false,
      error: response?.message || 'Failed to delete referral',
    };
  } catch (error) {
    console.error('Delete referral error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to delete referral. Please try again.';

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Get processed referrals
 * GET /v1/referrals/processed?skip=0&limit=20
 * @param {Object} params - Query params { skip?: number, limit?: number }
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const getProcessedReferrals = async (params = {}) => {
  try {
    const query = buildReferralQuery(params);

    const response = await api.get('/v1/referrals/processed', query);

    if (response?.status === 'success' && response?.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Processed referrals retrieved successfully',
      };
    }

    return {
      success: false,
      error: response?.message || 'Failed to retrieve processed referrals',
      data: null,
    };
  } catch (error) {
    console.error('Get processed referrals error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to retrieve processed referrals. Please try again.';

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get unprocessed referrals
 * GET /v1/referrals/unprocessed?skip=0&limit=20
 * @param {Object} params - Query params { skip?: number, limit?: number }
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const getUnprocessedReferrals = async (params = {}) => {
  try {
    const query = buildReferralQuery(params);

    const response = await api.get('/v1/referrals/unprocessed', query);

    if (response?.status === 'success' && response?.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Unprocessed referrals retrieved successfully',
      };
    }

    return {
      success: false,
      error: response?.message || 'Failed to retrieve unprocessed referrals',
      data: null,
    };
  } catch (error) {
    console.error('Get unprocessed referrals error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to retrieve unprocessed referrals. Please try again.';

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get failed referrals
 * GET /v1/referrals/failed?skip=0&limit=20
 * @param {Object} params - Query params { skip?: number, limit?: number }
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const getFailedReferrals = async (params = {}) => {
  try {
    const query = buildReferralQuery(params);

    const response = await api.get('/v1/referrals/failed', query);

    if (response?.status === 'success' && response?.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Failed referrals retrieved successfully',
      };
    }

    return {
      success: false,
      error: response?.message || 'Failed to retrieve failed referrals',
      data: null,
    };
  } catch (error) {
    console.error('Get failed referrals error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to retrieve failed referrals. Please try again.';

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get students by referral
 * GET /v1/referrals/{referral_id}/students
 */
export const getStudentsByReferral = async (referralId, params = {}) => {
  try {
    const query = {};
    if (params.skip !== undefined) query.skip = params.skip;
    if (params.limit !== undefined) query.limit = params.limit;

    const response = await api.get(`/v1/referrals/${referralId}/students`, query);

    if (response?.status === 'success' && response?.data !== undefined) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Referral students retrieved successfully',
      };
    }

    return {
      success: false,
      error: response?.message || 'Failed to retrieve referral students',
      data: null,
    };
  } catch (error) {
    console.error('Get referral students error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to retrieve referral students. Please try again.';

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get referral reports summary
 * GET /v1/referrals/reports/summary
 */
export const getReferralReportSummary = async () => {
  try {
    const response = await api.get('/v1/referrals/reports/summary');

    if (response?.status === 'success' && response?.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Referral report summary retrieved successfully',
      };
    }

    return {
      success: false,
      error: response?.message || 'Failed to retrieve referral report summary',
      data: null,
    };
  } catch (error) {
    console.error('Get referral report summary error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to retrieve referral report summary. Please try again.';

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Export referrals PDF
 * GET /v1/referrals/export/pdf
 * @param {Object} params - Query params { status_filter?: string, search?: string }
 */
export const exportReferralsPdf = async (params = {}) => {
  try {
    const query = {};
    if (params.status_filter) query.status_filter = params.status_filter;
    if (params.search) query.search = params.search;

    const queryString = new URLSearchParams(query).toString();
    const endpoint = `/v1/referrals/export/pdf${queryString ? `?${queryString}` : ''}`;
    const url = getApiUrl(endpoint);
    const token = getAuthToken();

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/pdf',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    }

    const contentDisposition = response.headers.get('content-disposition');
    let filename = 'referrals.pdf';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return {
      success: true,
      message: 'Referrals PDF exported successfully',
    };
  } catch (error) {
    console.error('Export referrals PDF error:', error);
    return {
      success: false,
      error: error?.message || 'Failed to export referrals PDF. Please try again.',
    };
  }
};

/**
 * Upload referrals CSV
 * POST /v1/referrals/upload-csv
 * @param {File} file - CSV file
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const uploadReferralsCsv = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.uploadFile('/v1/referrals/upload-csv', formData);

    if (response?.status === 'success' && response?.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'CSV uploaded successfully',
      };
    }

    return {
      success: false,
      error: response?.message || 'Failed to upload CSV',
      data: null,
    };
  } catch (error) {
    console.error('Upload referrals CSV error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to upload CSV. Please try again.';

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

const referralsService = {
  createReferral,
  getReferrals,
  getReferral,
  updateReferral,
  deleteReferral,
  getProcessedReferrals,
  getUnprocessedReferrals,
  getFailedReferrals,
  getStudentsByReferral,
  getReferralReportSummary,
  uploadReferralsCsv,
  exportReferralsPdf,
};

export default referralsService;
